import React from 'react';
import { Link } from 'react-router-dom';
import { SITE_CONFIG, PLATFORM_SOCIAL_LINKS, getMailtoUrl, getWhatsAppUrl } from '../../config/siteConfig';
import MarketingLink from '../MarketingLink/MarketingLink';
import { theme } from '../../theme/colors';
import { isPartnersHost } from '../../utils/hostNavigation';
import { useMarketingPlans } from '../../hooks/useMarketingPlans';
import { FaIcon, FooterLinkArrow } from './FooterIcons';
import { EmailIcon, PhoneIcon, PartnerIcon, WhatsAppIcon } from './FooterActionIcons';
import './footerIcons.css';
import AuthPlatformFooter from './AuthPlatformFooter';
import './PlatformFooter.css';

export type PlatformFooterVariant = 'default' | 'auth';

export interface PlatformFooterProps {
  variant?: PlatformFooterVariant;
}

const PlatformFooter: React.FC<PlatformFooterProps> = ({ variant = 'default' }) => {
  if (variant === 'auth' || isPartnersHost()) {
    return <AuthPlatformFooter />;
  }
  const year = new Date().getFullYear();
  const { pricingSummary } = useMarketingPlans();

  const companyLinks = [
    { label: 'About Us', href: '/about', marketing: true },
    { label: 'Pricing', href: '/pricing', marketing: true },
    { label: 'Partner with Us', href: '/partner/setup', marketing: false },
    { label: 'Blog', href: '/blog', marketing: true },
  ];

  const supportLinks = [
    { label: 'Help Center', href: '/help', marketing: true },
    { label: 'Contact Us', href: '/contact', marketing: true },
    { label: 'FAQs', href: '/faqs', marketing: true },
    { label: 'Terms & Conditions', href: '/terms-and-conditions', marketing: true },
    { label: 'Privacy Policy', href: '/privacy-policy', marketing: true },
  ];

  return (
    <footer id="footer" className="footer-platform">
      <div className="fp-inner">
        <div className="fp-top">
          <div className="fp-brand">
            <MarketingLink href="/" className="fp-logo" aria-label={SITE_CONFIG.name}>
              <span className="fp-logo-badge">
                <img src={theme.logo.url} alt={SITE_CONFIG.name} className="fp-logo-img" />
              </span>
            </MarketingLink>
            <p className="fp-tagline">
              {SITE_CONFIG.tagline}. Explore menus, order from the table, and run your
              restaurant from one cloud platform — no guest app required.
            </p>
            {pricingSummary && (
              <p className="fp-price-note">{pricingSummary}</p>
            )}
            <div className="fp-actions" aria-label="Contact Peppar">
              <a
                href={getMailtoUrl()}
                className="fp-action-btn fp-action-btn--email"
                title="Email us"
                aria-label="Email us"
              >
                <EmailIcon />
                <span className="fp-action-label">Email</span>
              </a>
              <a
                href={`tel:${SITE_CONFIG.contact.phoneTel}`}
                className="fp-action-btn fp-action-btn--phone"
                title="Call us"
                aria-label="Call us"
              >
                <PhoneIcon />
                <span className="fp-action-label">Call</span>
              </a>
              <a
                href={getWhatsAppUrl()}
                className="fp-action-btn fp-action-btn--whatsapp"
                title="WhatsApp"
                aria-label="Chat on WhatsApp"
                target="_blank"
                rel="noopener noreferrer"
              >
                <WhatsAppIcon />
                <span className="fp-action-label">WhatsApp</span>
              </a>
              <a
                href={SITE_CONFIG.urls.partners}
                className="fp-action-btn fp-action-btn--partner"
                title="Partner portal"
                aria-label="Partner portal"
                target="_blank"
                rel="noopener noreferrer"
              >
                <PartnerIcon />
                <span className="fp-action-label">Partners</span>
              </a>
            </div>
            <div className="fp-social" aria-label="Follow Peppar">
              {PLATFORM_SOCIAL_LINKS.map((link) => (
                <a
                  key={link.key}
                  href={link.url}
                  className={`fp-social-btn ${link.className}`.trim()}
                  title={link.label}
                  aria-label={link.label}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaIcon icon={link.icon} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="fp-col-title">Company</div>
            <ul className="fp-links">
              {companyLinks.map((link) => (
                <li key={link.label}>
                  {link.marketing ? (
                    <MarketingLink href={link.href}>
                      {link.label} <FooterLinkArrow />
                    </MarketingLink>
                  ) : (
                    <Link to={link.href}>
                      {link.label} <FooterLinkArrow />
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="fp-col-title">Support</div>
            <ul className="fp-links">
              {supportLinks.map((link) => (
                <li key={link.label}>
                  <MarketingLink href={link.href}>
                    {link.label} <FooterLinkArrow />
                  </MarketingLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="fp-col fp-col-contact">
            <div className="fp-col-title">Contact</div>
            <ul className="fp-links fp-contact-links">
              <li>
                <a href={getMailtoUrl()}>
                  <FaIcon icon="envelope-o" className="fp-inline-icon" />
                  {SITE_CONFIG.contact.email}
                </a>
              </li>
              <li>
                <a href={`tel:${SITE_CONFIG.contact.phoneTel}`}>
                  <FaIcon icon="phone" className="fp-inline-icon" />
                  {SITE_CONFIG.contact.phoneDisplay}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="fp-bottom">
          <div className="fp-copy">
            © {year}. All Rights Reserved by {SITE_CONFIG.legalName}
          </div>
          <div className="fp-contact">
            <a href={getMailtoUrl()} className="fp-contact-item">
              <FaIcon icon="envelope-o" />
              {SITE_CONFIG.contact.email}
            </a>
            <a href={`tel:${SITE_CONFIG.contact.phoneTel}`} className="fp-contact-item">
              <FaIcon icon="phone" />
              {SITE_CONFIG.contact.phoneDisplay}
            </a>
          </div>
          <div className="fp-legal">
            <MarketingLink href="/terms-and-conditions">Terms &amp; Conditions</MarketingLink>
            <MarketingLink href="/privacy-policy">Privacy Policy</MarketingLink>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default PlatformFooter;
